import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { cn } from '@/lib/cn';

/**
 * Kinetic's footer grid, by how many link columns stand beside the wordmark.
 * Written out because Tailwind reads class names out of the source and never
 * sees a string built at runtime.
 */
const KINETIC_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[2fr_1fr]',
  2: 'lg:grid-cols-[2fr_1fr_1fr]',
  3: 'lg:grid-cols-[2fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]',
};

/** One footer column: a heavy italic capital heading over its links. */
function KineticColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-lg font-extrabold uppercase italic leading-none"
        style={{ fontFamily: 'var(--storefront-heading-font)', color: 'var(--storefront-text)' }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The close of a dark page: the same near-black as the page, set off by a
 * hairline, with the wordmark and the shop's line on the left and the footer
 * menu, the shelves and the pages beside it - each skipped when empty and the
 * grid sized to what is left - then the fine print under a second hairline.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer
      className="border-t pt-16 text-[14px]"
      style={{ borderColor: 'var(--storefront-surface)', color: 'var(--storefront-muted)' }}
    >
      <StorefrontContainer
        className={cn('grid gap-10 pb-14 text-start sm:grid-cols-2', KINETIC_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-3xl font-extrabold uppercase italic leading-none"
            style={{ fontFamily: 'var(--storefront-heading-font)', color: 'var(--storefront-text)' }}
          >
            {storeName}
            <span aria-hidden style={{ color: 'var(--storefront-primary)' }}>
              .
            </span>
          </p>
          {description ? <p className="mt-5 max-w-[24rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <KineticColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:text-[color:var(--storefront-text)]"
                />
              ))}
            </nav>
          </KineticColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <KineticColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-2.5"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="hover:text-[color:var(--storefront-text)]"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </KineticColumn>
        ))}
        {preview ? null : (
          <KineticColumn title={copy('theme.footer_support')}>
            <PageLinks className="mb-2.5 flex-col items-start justify-start gap-2.5" />
            <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </KineticColumn>
        )}
      </StorefrontContainer>
      <div className="border-t" style={{ borderColor: 'var(--storefront-surface)' }}>
        <StorefrontContainer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-5 text-[12px]">
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          <span style={copyrightStyle(footer)}>
            © {new Date().getFullYear()} {storeName} ·{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </StorefrontContainer>
      </div>
    </footer>
  );
};
